import React from 'react';
import { Datestamp } from 'vl-common/src/components/Datestamp';
import { differenceInCalendarDays, subDays, format, endOfToday, startOfToday } from 'date-fns';

export enum AuditedCategory {
  cases = 'cases',
  appointments = 'appointments',
  tasks = 'tasks',
  outcomes = 'outcomes'
}

export enum AuditedDetailsCategory {
  cases = 'cases',
  appointments = 'appointments',
  tasks = 'tasks',
  outcomes = 'outcomes',
  user = 'user'
}

export const defaultStartDate = format(subDays(new Date(startOfToday()), 1), 'yyyy-MM-dd');
export const defaultEndDate = format(new Date(endOfToday()), 'yyyy-MM-dd');

const MAX_PERIOD = 14;
export const isOutOfAllowedPeriod = (startDate: string, endDate: string) => {
  return differenceInCalendarDays(new Date(endDate), new Date(startDate)) > MAX_PERIOD;
};

export const getAuditSummaryColumns = (allCategories: any, filterableByDate = true) => [
  {
    display: 'Date & Time',
    dataSourceKey: 'created_date',
    columnDataFormat: ({ date }: { date: Date }) => (
      <Datestamp date={date} format="Date @ Time (short, no year, 24h) - Fri, Mar 3rd @ 08:57" />
    ),
    filterable: filterableByDate && {
      queryKey: 'created_date',
      dateStartFilterKey: 'start_date',
      dateEndFilterKey: 'end_date'
    },
    dateFilter: true
  },
  {
    display: 'Created by',
    dataSourceKey: 'created_by'
  },
  {
    display: 'Category',
    dataSourceKey: 'category',
    filterMultiple: true,
    filterKey: 'categories',
    filters: [...allCategories].map((item) => ({ value: item, text: item }))
  },
  {
    display: 'Key',
    dataSourceKey: 'key'
  }
];

export function normaliseDate(date: string): Date {
  return new Date(date + 'Z');
}

function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
  });
}

export function transformRecords(record: any) {
  return Object.fromEntries(
    Object.entries(record).map(([key, val]) => {
      const newKey = toTitleCase(key.replaceAll('_', ' '));
      let newVal = newKey.includes('Date') && val ? normaliseDate(val as string) : val;
      if (newVal === null || newVal === 'null') {
        newVal = '';
      }
      return [newKey, newVal];
    })
  );
}

export function transformAuditDetails(list: any) {
  if (!list?.length) {
    return [];
  }

  return list.map((item: any) => ({
    'Amended Date': normaliseDate(item.created_date),
    'Amended By': item.created_by,
    ...transformRecords(item.record)
  }));
}

export const getAuditDetailColumns = (record: any) => {
  return Object.keys(record).map((key) => ({
    display: key,
    dataSourceKey: key,
    columnDataFormat: key.includes('Date')
      ? ({ data }: { data: Date }) => {
          return data ? (
            <Datestamp date={data} format="Date @ Time (short, no year, 24h) - Fri, Mar 3rd @ 08:57" />
          ) : (
            ''
          );
        }
      : undefined
  }));
};

export const allAuditCategories = new Set([
  AuditedCategory.appointments,
  AuditedCategory.outcomes,
  AuditedCategory.tasks,
  AuditedCategory.cases
]);

export function getItemKey(keys: any, item: any) {
  const itemKeys: any = [];
  Object.values(keys).forEach((val) => {
    if (val) {
      itemKeys.push(item[val as string]);
    }
  });
  return itemKeys.join('/');
}
